import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import useMyMenu from './useMyMenu';
import { buildCatalogueIconIndex, iconFor } from 'menu-items/dynamicMenu';

// Normalise a URL for matching the same way the sidebar does: drop the query
// string / hash and any trailing slash so `/admin/materials` and
// `/admin/materials/` compare equal. Kept in step with normUrl in dynamicMenu.js.
const normUrl = (u) => (typeof u === 'string' ? u.split('?')[0].split('#')[0].replace(/\/+$/, '') : '');

/**
 * The icon an administrator configured for the current page in Features and
 * Components, as an Ant Design component ready to render.
 *
 * This is the page-header counterpart to applyCatalogueIcons in the sidebar: both
 * read the SAME catalogue index (GET /catalogue/my-menu), so a page's banner icon
 * and its sidebar item can no longer drift apart. Before this, every page banner
 * hard-coded its own icon, so editing the icon in Features and Components changed
 * the sidebar but not the header.
 *
 * Resolution order:
 *   1. An explicit catalogue `code` when the caller knows it.
 *   2. The current route, matched against the catalogue's page/feature URLs. The
 *      exact path is tried first, then successively shorter parent paths, so a
 *      detail route (`/supervisor/child/CLT-123`) still resolves to the icon its
 *      parent menu item (`/supervisor/child`) carries.
 *
 * Falls back to `fallback` (the page's own hard-coded icon) whenever the catalogue
 * has no entry for this route — not yet loaded, or a page that is not a menu
 * destination — so nothing ever renders without an icon.
 *
 * @param {import('react').ElementType|null} fallback Icon component to use when the catalogue has none.
 * @param {string|null} code Optional catalogue feature/page code to look up directly.
 * @returns {import('react').ElementType|null} An icon component (Ant Design), or `fallback`.
 */
export default function usePageIcon(fallback = null, code = null) {
  const myMenu = useMyMenu();
  const { pathname } = useLocation();

  return useMemo(() => {
    const index = buildCatalogueIconIndex(myMenu);
    if (!index) return fallback;

    let name = null;
    if (code && index.byCode[code]) name = index.byCode[code];

    if (!name) {
      // Try the exact path, then walk up parent segments so nested routes inherit
      // their parent menu item's icon. The longest (most specific) match wins.
      const segs = normUrl(pathname).split('/');
      while (segs.length > 1 && !name) {
        name = index.byUrl[segs.join('/')];
        if (!name) segs.pop();
      }
    }

    return name ? iconFor(name) : fallback;
  }, [myMenu, pathname, fallback, code]);
}
